import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';

import EntityFormPage, { EntityForm } from '@entity/EntityForm/EntityForm';
import getGraphqlData from '@boilerplate/lib/getGraphqlData';
import { BaseFormUpdateProps } from '@boilerplate/types/entity';
import { addEntityToRelatedDataOptions } from '@boilerplate/lib/relatedDataToOptions';
import Notistack from '@/lib/notistack';
import DaysOffEntity from '@/entities/daysOff';
import { catchGraphQlError } from '@/lib/catchError';

  import UserEntity from '@/entities/user';
  import TenantEntity from '@/entities/tenant';

function DaysOffBaseUpdateForm({ formOnly = false, onClose, Entity = DaysOffEntity, id: propId, ...props }: BaseFormUpdateProps<typeof DaysOffEntity.model>) {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const id = propId || paramId;

  const Component = formOnly ? EntityForm : EntityFormPage;

  const { loading, data } = DaysOffEntity.model.useGet({
    variables: {
      id,
            withUser: true,
            withTenant: true,
    },
  });

  const [updateMutation, { loading: updateLoading }] = DaysOffEntity.model.useUpdate();

  const relationsOptionsQuery = DaysOffEntity.model.useRelationsOptions({
    variables: {
          withUsers: true,
          withTenants: true,
    }
  });

  const relationsOptions = useMemo(
    () =>
      addEntityToRelatedDataOptions(relationsOptionsQuery, [
              UserEntity,
              TenantEntity,
      ]),
    [relationsOptionsQuery]
  );

  const handleSubmit = (newData) => {
    newData.variables = getGraphqlData(Entity, newData.variables, 'update');

    updateMutation(newData)
      .then((result) => {
        Notistack.toast(t('crud:updatedItem', { item: t('entities:daysOff') }), { variant: 'success' });

        if (!formOnly) {
          navigate(-1);
        }

        return onClose?.(result.data?.updateDaysOff);
      })
      .catch(catchGraphQlError);
  };

  if (loading || relationsOptionsQuery.loading) {
    return <CircularProgress />;
  }

  return (
    <Component
      {...props}
      type="update"
      Entity={Entity}
      loading={updateLoading}
      handleSubmit={handleSubmit}
      relationsOptions={relationsOptions}
      data={data.daysOff}
    />
  );
}

export default DaysOffBaseUpdateForm;
